import React, { useState } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

const NonLoggedInTopNavPage = ({ mode = "light", onToggleTheme }) => {
  const location = useLocation();
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);

  const hideLoginButton = ["/login", "/signup"].includes(location.pathname);
  const isDark = mode === "dark";
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const navTextColor = "var(--ink-7)";
  const navStrongColor = "var(--ink-9)";

  return (
    <AppBar
      position="fixed"
      sx={{
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: "transparent",
        backgroundImage: "none",
        boxShadow: "none",
        border: "none",
        outline: "none",
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 56,
          pt: "env(safe-area-inset-top)",
          px: 1.5,
          backgroundColor: "transparent",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1360px",
            mx: "auto",
            px: { xs: 1.2, md: 2 },
            py: 0.75,
            borderRadius: "var(--r-pill)",
            background:
              "color-mix(in oklab, var(--ink-0) 82%, transparent)",
            border: "1px solid var(--ink-3)",
            boxShadow: "var(--shadow-2)",
            backdropFilter: "blur(12px) saturate(140%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <RouterLink
              to="/"
              style={{ display: "flex", alignItems: "center", textDecoration: "none", color: "inherit" }}
            >
              <Box
                component="img"
                src="/infrablaze-logo.png"
                alt="Infrablaze Logo"
                sx={{ height: 32, mr: 0.5 }}
              />
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 800,
                  color: navStrongColor,
                  fontSize: "1.2rem",
                  lineHeight: 1,
                }}
              >
                Infra
                <Box component="span" sx={{ color: "var(--amber)" }}>
                  Blaze
                </Box>
              </Typography>
            </RouterLink>
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, alignItems: "center" }}>
            {[
              { to: "/product", label: "Product" },
              {
                to: "/blaze-ai",
                label: (
                  <>
                    Blaze
                    <Box component="span" sx={{ color: "var(--amber)" }}>
                      AI
                    </Box>
                  </>
                ),
              },
              { to: "/pricing", label: "Pricing" },
              { to: "/docs", label: "Docs" },
              { to: "/faqs", label: "FAQs" },
              { to: "/about", label: "About" },
              { to: "/contact", label: "Contact" },
            ].map((item) => {
              const active =
                location.pathname === item.to ||
                (item.to === "/product" && location.pathname.startsWith("/product"));
              return (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  disableRipple
                  sx={{
                    color: active ? navStrongColor : navTextColor,
                    fontSize: "0.82rem",
                    fontWeight: active ? 700 : 500,
                    px: 1.4,
                    py: 0.5,
                    minHeight: 32,
                    borderRadius: "var(--r-pill)",
                    textTransform: "none",
                    position: "relative",
                    backgroundColor: active ? "var(--teal-wash)" : "transparent",
                    "&::after": active
                      ? {
                          content: '""',
                          position: "absolute",
                          bottom: 2,
                          left: "30%",
                          right: "30%",
                          height: 2,
                          borderRadius: 2,
                          background:
                            "linear-gradient(90deg, transparent, var(--teal), transparent)",
                        }
                      : {},
                    "&:hover": {
                      backgroundColor: "var(--ink-2)",
                      color: navStrongColor,
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.9, alignItems: "center", ml: 1.2 }}>
            <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
              <IconButton
                onClick={onToggleTheme}
                size="small"
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: "var(--r-pill)",
                  color: navTextColor,
                  border: "1px solid var(--ink-3)",
                  background: "var(--ink-1)",
                  "&:hover": {
                    background: "var(--ink-2)",
                    color: navStrongColor,
                  },
                }}
              >
                {isDark ? <LightModeIcon sx={{ fontSize: 16 }} /> : <DarkModeIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
            {!hideLoginButton && (
              <Tooltip
                title="Now accepting design partners — InfraBlaze is currently in private beta."
                arrow
                placement="bottom"
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: "var(--f-mono)",
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    textTransform: "none",
                    borderRadius: "var(--r-pill)",
                    height: 28,
                    minHeight: 28,
                    px: 1.4,
                    py: 0,
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "help",
                    color: "var(--teal-dim)",
                    fontSize: "0.72rem",
                    border: "1px solid color-mix(in oklab, var(--teal) 25%, transparent)",
                    background: "var(--teal-wash)",
                    transition: "background 0.2s ease, color 0.2s ease",
                    "&:hover": {
                      background: "color-mix(in oklab, var(--teal) 12%, transparent)",
                      color: "var(--teal)",
                    },
                  }}
                >
                  Private Beta · Invite Only
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              onClick={(event) => setMobileMenuAnchorEl(event.currentTarget)}
              aria-label="Open navigation menu"
              sx={{ color: navTextColor }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          <Menu
            anchorEl={mobileMenuAnchorEl}
            open={isMobileMenuOpen}
            onClose={() => setMobileMenuAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {!hideLoginButton ? (
              <MenuItem
                disabled
                sx={{
                  opacity: "1 !important",
                  pointerEvents: "none",
                  mx: 1,
                  my: 0.5,
                  borderRadius: "var(--r-pill)",
                  border: "1px solid color-mix(in oklab, var(--teal) 25%, transparent)",
                  color: "var(--teal-dim) !important",
                  fontFamily: "var(--f-mono)",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  justifyContent: "center",
                  py: 0.62,
                  background: "var(--teal-wash)",
                  "&.Mui-disabled": {
                    color: "var(--teal-dim) !important",
                  },
                }}
              >
                Private Beta · Invite Only
              </MenuItem>
            ) : null}
            <MenuItem
              component={RouterLink}
              to="/product"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              Product
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/blaze-ai"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              Blaze
              <Box component="span" sx={{ color: "var(--amber)" }}>
                AI
              </Box>
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/pricing"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              Pricing
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/docs"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              Docs
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/faqs"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              FAQs
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/about"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              About
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/contact"
              onClick={() => setMobileMenuAnchorEl(null)}
            >
              Contact
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMobileMenuAnchorEl(null);
                onToggleTheme && onToggleTheme();
              }}
            >
              {isDark ? "Switch to light mode" : "Switch to dark mode"}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default NonLoggedInTopNavPage;
